import { FlagStates } from '@generated/graphql';
import Check from '@mui/icons-material/Check';
import { Box } from '@ovComponents/1-primative/box/box';
import { Button } from '@ovComponents/2-component/button/button';
import { SideDrawer } from '@ovComponents/2-component/sideDrawer/sideDrawer';
import {
  ConfirmationModal,
  ConfirmButtonColors,
} from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasUnsavedFlagNoteDraft } from '../utils/reconFlagNotes';
import { getReconFlagsDetailDrawerTokens } from './reconFlagsDetailDrawer.tokens';
import type { ReconFlagsDetailDrawerProps } from './reconFlagsDetailDrawer.types';
import { ReconFlagsDetailDrawerBody } from './reconFlagsDetailDrawerBody';

export type { ReconFlagsDetailDrawerProps } from './reconFlagsDetailDrawer.types';

export const ReconFlagsDetailDrawer = ({
  sys,
  flag,
  isOpen,
  onClose,
  notesDraft,
  onNotesDraftChange,
  onNotesSaved,
  onResolve,
  onDismiss,
  resolveLoading,
  dismissLoading,
}: ReconFlagsDetailDrawerProps) => {
  const { t } = useTranslation(['reconciliation', 'shared']);
  const tokens = useMemo(() => getReconFlagsDetailDrawerTokens(sys), [sys]);
  const [discardDraftConfirmOpen, setDiscardDraftConfirmOpen] = useState(false);

  const transitionInFlight = resolveLoading || dismissLoading;
  const actionsUnavailable =
    flag == null || flag.state !== FlagStates.Flagged || transitionInFlight;

  const handleCloseRequest = useCallback(() => {
    if (flag != null && hasUnsavedFlagNoteDraft(notesDraft, flag.notes)) {
      setDiscardDraftConfirmOpen(true);
      return;
    }
    onClose();
  }, [flag, notesDraft, onClose]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardDraftConfirmOpen(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setDiscardDraftConfirmOpen(false);
    }
  }, [isOpen]);

  const footer = flag != null && (
    <Box sx={tokens.footerStack}>
      <Box
        sx={[
          tokens.footerRow,
          tokens.blockHorizontalInset,
          { py: 1.5, boxSizing: 'border-box', justifyContent: 'flex-end' },
        ]}
      >
        <Box display='flex' gap={1}>
          <Button
            label={t('reconFlags.selection.dismiss')}
            variant='outlined'
            color='primary'
            size='sm'
            disabled={actionsUnavailable || resolveLoading}
            loading={dismissLoading}
            onClick={() => void onDismiss()}
          />
          <Button
            label={t('reconFlags.flagState.FIXED')}
            variant='filled'
            color='primary'
            size='sm'
            leadingIcon={Check}
            disabled={actionsUnavailable || dismissLoading}
            loading={resolveLoading}
            onClick={() => void onResolve()}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        onClose={handleCloseRequest}
        closeOnOutsideClick
        title={t('reconFlags.drawer.title')}
        titleComponent='h3'
        titleVariant='headingXSmall'
        footer={footer}
        bodySx={{ px: 0, py: 0 }}
        footerSx={{ p: 0 }}
      >
        {flag && (
          <ReconFlagsDetailDrawerBody
            key={flag.id}
            sys={sys}
            flag={flag}
            t={t}
            notesDraft={notesDraft}
            onNotesDraftChange={onNotesDraftChange}
            onNotesSaved={onNotesSaved}
            notesDisabled={transitionInFlight}
          />
        )}
      </SideDrawer>
      <ConfirmationModal
        open={discardDraftConfirmOpen}
        title={t('reconFlags.drawer.discardDraftConfirmTitle')}
        bodyText={t('reconFlags.drawer.discardDraftConfirmBody')}
        confirmButtonLabel={t('reconFlags.drawer.discardDraftConfirmAction')}
        cancelButtonLabel={t('shared:cancel')}
        confirmButtonColor={ConfirmButtonColors.destructive}
        onConfirm={handleConfirmDiscard}
        onCancel={() => setDiscardDraftConfirmOpen(false)}
        testIdPrefix='recon-flag-discard-draft'
      />
    </>
  );
};
